import { api } from '@/utils/api';
import { IonFab, IonFabButton, IonSpinner } from '@ionic/react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import GolfIcon from '../GolfIcon';

export interface GolfClubMapItem {
  id: string;
  name: string;
  city: string;
  country: string;
  state?: string;
  latitude: number;
  longitude: number;
  distance?: number;
  slug?: string;
  holes?: number;
  amenities?: string[];
}

interface FilterOptions {
  minHoles?: number;
  maxHoles?: number;
  country?: string;
  state?: string;
  query?: string;
  sortBy?: string;
  amenities?: string[];
}

interface ClubSearchMapProps {
  mapContainerRef: React.RefObject<HTMLDivElement>;
  className?: string;
  location: { latitude: number; longitude: number } | null;
  onCourseSelect?: (course: GolfClubMapItem) => void;
  onClickMap?: () => void;
  handleLocateUser?: () => void;
  filters?: FilterOptions;
  onViewportChange?: (bounds: mapboxgl.LngLatBounds | null) => void;
  withCourses?: boolean;
  isLoading?: boolean;
}

const ClubSearchMap: React.FC<ClubSearchMapProps> = ({
  className,
  mapContainerRef,
  location,
  onCourseSelect,
  onClickMap,
  handleLocateUser,
  filters,
  onViewportChange,
  withCourses = false,
  isLoading
}) => {
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const markersRef = useRef<mapboxgl.Marker[]>([]);
  const [isMapInitialized, setIsMapInitialized] = useState(false);
  const [viewportCourses, setViewportCourses] = useState<GolfClubMapItem[]>([]);
  const [isViewportLoading, setIsViewportLoading] = useState<boolean>(false);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!mapContainerRef.current) return;

    mapboxgl.accessToken = 'pk.eyJ1IjoieGRpc3A1MDkiLCJhIjoiY20xcndxbXJvMDA2djJtczd6cDAxczR6diJ9.jbNicBwTcJuu0XHVIzZO6w';

    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: 'mapbox://styles/mapbox/streets-v12',
      center: location ? [location.longitude, location.latitude] : [2, 46],
      zoom: location ? 11 : 4,
      minZoom: 2,
    });

    mapRef.current = map;

    map.on('load', () => {
      setIsMapInitialized(true);

      map.loadImage(
        '/assets/images/icons/club_pin.webp',
        (error, image) => {
          if (error) throw error;
          if (!image) return;

          map.addImage('club', image);

          // Handle moveend event to update viewport bounds
          map.on('moveend', debounceMapMoved);

          // Initial fetch if we have filters or location
          if (location || filters) {
            const bounds = map.getBounds();
            fetchCoursesInViewport(bounds);
          }

        }
      );
    });

    map.on('style.load', (map) => map.target.resize());

    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      clearMarkers();
      map.remove();
    };
  }, [mapContainerRef, location]);

  const debounceMapMoved = useCallback(() => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

    debounceTimerRef.current = setTimeout(() => {
      if (!mapRef.current) return;
      const bounds = mapRef.current.getBounds();

      if (onViewportChange) {
        onViewportChange(bounds);
      } else {
        fetchCoursesInViewport(bounds);
      }
    }, 500);
  }, [onViewportChange]);

  useEffect(() => {
    if (!mapRef.current || !isMapInitialized) return;
    const map = mapRef.current;

    map.on('moveend', debounceMapMoved);
    map.on('zoomend', debounceMapMoved);

    return () => {
      map.off('moveend', debounceMapMoved);
      map.off('zoomend', debounceMapMoved);
    };
  }, [debounceMapMoved, isMapInitialized]);

  useEffect(() => {
    if (!mapRef.current || !location || !isMapInitialized) return;
    const map = mapRef.current;

    map.flyTo({
      center: [location.longitude, location.latitude],
      zoom: 10,
      speed: 1.5,
      curve: 1.5,
      essential: true
    });

    setTimeout(() => {
      if (mapRef.current) {
        fetchCoursesInViewport(mapRef.current.getBounds());
      }
    }, 1000);
  }, [location, isMapInitialized]);

  useEffect(() => {
    if (!mapRef.current || !isMapInitialized || !filters) return;
    fetchCoursesInViewport(mapRef.current.getBounds());
  }, [filters, isMapInitialized]);

  useEffect(() => {
    if (!mapRef.current || !isMapInitialized) return;
    addCoursesToMap(viewportCourses);
  }, [viewportCourses, isMapInitialized]);

  const fetchCoursesInViewport = async (bounds: mapboxgl.LngLatBounds | null) => {
    if (!bounds || !mapRef.current) return;
    setIsViewportLoading(true);

    try {
      const params: Record<string, string> = {
        limit: '50',
        bounds: `${bounds.getSouthWest().lng},${bounds.getSouthWest().lat},${bounds.getNorthEast().lng},${bounds.getNorthEast().lat}`
      };

      if (filters) {
        if (filters.minHoles) params.minHoles = filters.minHoles.toString();
        if (filters.maxHoles) params.maxHoles = filters.maxHoles.toString();
        if (filters.country) params.country = filters.country;
        if (filters.state) params.state = filters.state;
        if (filters.query) params.query = filters.query;
        if (filters.sortBy) params.sortBy = filters.sortBy;
        if (filters.amenities?.length) params.amenities = filters.amenities.join(',');
      }

      if (location) {
        params.latitude = location.latitude.toString();
        params.longitude = location.longitude.toString();
      }

      if (withCourses) params.withCourses = 'true';

      const response = await api.get('/golf-clubs', { params });

      let newCourses = [];
      if (response.data) {
        if (Array.isArray(response.data)) {
          newCourses = response.data;
        } else if (response.data.clubs && Array.isArray(response.data.clubs)) {
          newCourses = response.data.clubs;
        } else if (typeof response.data === 'object') {
          const possibleArrays = Object.values(response.data).filter(val => Array.isArray(val));
          if (possibleArrays.length > 0) newCourses = possibleArrays[0];
        }
      }

      setViewportCourses(newCourses);
    } catch (error) {
      console.error('Error fetching courses in viewport:', error);
    } finally {
      setIsViewportLoading(false);
    }
  };

  const clearMarkers = () => {
    markersRef.current.forEach(marker => marker.remove());
    markersRef.current = [];
  };

  const addCoursesToMap = (courses: GolfClubMapItem[]) => {
    const map = mapRef.current;
    if (!map || !courses || !Array.isArray(courses)) return;

    clearMarkers();

    courses.forEach(course => {
      if (!course.latitude || !course.longitude) return;

      const el = document.createElement('div');
      el.className = 'course-marker';
      el.style.backgroundImage = 'url(/assets/images/icons/club_pin.webp)';
      el.style.width = '32px';
      el.style.height = '32px';
      el.style.backgroundSize = 'cover';
      el.style.cursor = 'pointer';

      // Ensure we have valid coordinates
      const longitude = course.longitude || 0;
      const latitude = course.latitude || 0;

      // Create popup
      const popup = new mapboxgl.Popup({ offset: 25, closeButton: false })
        .setLngLat([longitude, latitude])
        .setHTML(`
        <div class="popup-content" data-course-id="${course.id}">
            <p>${course.name}</p>
            <p>${course.city}, ${course.country}</p>
        </div>
        `)
        .addTo(map);

      // Create the marker
      const marker = new mapboxgl.Marker(el)
        .setLngLat([longitude, latitude])
        .setPopup(popup)
        .addTo(map);

      el.addEventListener('click', () => {
        map.flyTo({
          center: [longitude, latitude],
          speed: 1.2,
          curve: 1,
          essential: true
        });
      });

      popup.on('open', () => {
        const popupContent = document.querySelector(`.popup-content[data-course-id="${course.id}"]`);
        if (popupContent) {
          popupContent.addEventListener('click', () => {
            map.flyTo({
              center: [longitude, latitude],
              speed: 1.2,
              curve: 1,
              essential: true
            });

            if (onCourseSelect) onCourseSelect(course);
          });
          popupContent.setAttribute('style', 'cursor: pointer;');
        }
      });

      markersRef.current.push(marker);
    });
  };

  return (
    <div className={`relative w-full h-full ${className || ''}`}>
      <div ref={mapContainerRef} className="w-full h-full" onClick={onClickMap} />

      {isLoading && (
        <div className="absolute top-0 left-0 size-full flex justify-center items-center bg-secondary z-10">
          <IonSpinner name="dots" />
        </div>
      )}

      {isViewportLoading && (
        <div className="absolute top-safe-offset-16 left-1/2 transform -translate-x-1/2 z-20 bg-secondary/80 backdrop-blur-sm rounded-full py-2 px-4 flex items-center gap-2 shadow-lg">
          <IonSpinner name="dots" />
        </div>
      )}

      {handleLocateUser && (
        <IonFab vertical="bottom" horizontal="end" slot="fixed" className="mb-4 mr-4">
          <IonFabButton color="light" onClick={handleLocateUser} size="small">
            <GolfIcon name='gps' />
          </IonFabButton>
        </IonFab>
      )}
    </div>
  );
};

export default ClubSearchMap;